import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CONTENT } from "@/constants/contentTestIds";

/**
 * Brand Content — embedded launcher inside the Brand Kit. Lists this brand's
 * content categories, lets admins create a new category inline, and deep-links
 * into the full content-authoring pages.
 */
export default function ContentTab({ brandId, canManage = true }) {
  const navigate = useNavigate();
  const [categories, setCategories] = useState(null);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", description: "" });
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get(`/brands/${brandId}/content-categories`)
      .then(({ data }) => setCategories(data))
      .catch((e) => setError(e.response?.data?.detail || "Could not load content"));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [brandId]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post(`/brands/${brandId}/content-categories`, form);
      setCreating(false);
      setForm({ name: "", slug: "", description: "" });
      navigate(`/brands/${brandId}/content/${data.category_id}`);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not create category");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="brand-kit-content-tab">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl">Brand Content</h2>
          <p className="text-sm text-[#3D3A35] mt-2 max-w-2xl">
            The brand&rsquo;s approved copy, organised by category. Open a category to
            add or edit the items the AI draws from — or add a new category.
          </p>
        </div>
        {canManage && !creating && (
          <button
            data-testid={CONTENT.newCategoryButton}
            onClick={() => setCreating(true)}
            className="shrink-0 px-5 py-2.5 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm font-medium"
          >
            New category
          </button>
        )}
      </div>

      {creating && (
        <form onSubmit={submit} className="border border-[#1A1F2C]/10 bg-white rounded-lg p-6 mt-6">
          <h3 className="font-serif text-xl mb-4">New content category</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <LField
              label="Name"
              testid={CONTENT.newCategoryName}
              value={form.name}
              onChange={(v) =>
                setForm((f) => ({
                  ...f,
                  name: v,
                  slug: f.slug || v.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, ""),
                }))
              }
              required
            />
            <LField
              label="Slug (snake_case)"
              testid={CONTENT.newCategorySlug}
              value={form.slug}
              onChange={(v) => setForm((f) => ({ ...f, slug: v }))}
              required
            />
          </div>
          <div className="mt-4">
            <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6358] mb-1">
              Description (optional)
            </label>
            <textarea
              data-testid={CONTENT.newCategoryDesc}
              rows={2}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
            />
          </div>
          {error && <p className="text-sm text-[#B5462F] mt-3">{error}</p>}
          <div className="flex gap-3 mt-5">
            <button
              type="submit"
              data-testid={CONTENT.newCategorySubmit}
              disabled={saving}
              className="px-5 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm font-medium disabled:opacity-60"
            >
              {saving ? "Creating…" : "Create category"}
            </button>
            <button
              type="button"
              data-testid={CONTENT.newCategoryCancel}
              onClick={() => { setCreating(false); setError(""); }}
              className="px-5 py-2 text-sm text-[#6B6358]"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {error && !creating && <p className="text-sm text-[#B5462F] mt-3">{error}</p>}

      {categories == null ? (
        <p className="text-[#6B6358] mt-6">Loading…</p>
      ) : categories.length === 0 ? (
        <p className="text-[#6B6358] text-sm italic mt-6">
          No content categories yet.{canManage ? " Click “New category” to add one." : ""}
        </p>
      ) : (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {categories.map((c) => (
            <Link
              key={c.category_id}
              to={`/brands/${brandId}/content/${c.category_id}`}
              data-testid={`brand-kit-content-row-${c.category_id}`}
              className="border border-[#1A1F2C]/10 bg-white rounded-lg px-4 py-4 hover:border-[#1A1F2C]/30 transition-colors"
            >
              <div className="text-base font-medium text-[#1A1F2C]">{c.name}</div>
              {c.description && (
                <div className="text-xs text-[#6B6358] mt-1">{c.description}</div>
              )}
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function LField({ label, testid, value, onChange, required }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6358] mb-1">{label}</label>
      <input
        data-testid={testid}
        type="text"
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
      />
    </div>
  );
}
